import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EllipsisVertical, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { updateCharacter } from 'app/frontend/base/api/character'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  removeTeamMember,
  updateManualMemberClass,
} from 'app/frontend/base/api/team'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import capitalize from 'lodash/capitalize'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import CharacterAvatar from 'app/frontend/base/components/CharacterAvatar'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useClaimExistingManualCharacter } from 'app/frontend/base/config/mutations'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useRefreshCharacterDetails } from 'app/frontend/base/hooks/useRefreshCharacterDetails'
import { useTeamMemberEditor } from '../hooks'
import {
  RankMenuItems,
  MainSpecMenuItems,
  OffSpecMenuItems,
  ClassMenuItems,
} from './TeamMemberEditMenus'

type Props = {
  member: Partial<wowaudit.TeamMembership>
  size?: 'small' | 'medium'
}

const TeamMemberContextMenu = ({ member, size = 'medium' }: Props) => {
  const { t } = useTranslation()
  const { guild } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const [open, setOpen] = useState(false)

  const { mutate: removeFromTeam, isPending: removing } = useMutation({
    mutationFn: (memberId: number) => removeTeamMember(team.id, memberId),
    onSuccess: (newData) => {
      toast.success(
        t('dashboard.team.roster.member-removed', {
          name: member.characterReference?.name,
        }),
      )
      queryClient.setQueryData(['team', { path: team.path }], newData)
      setOpen(false)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const queryClient = useQueryClient()
  const { team } = useTeam()
  const gameVersion = useGameVersion()
  const { mutate: changeClass, isPending: changingClass } = useMutation({
    mutationFn: (characterClass: CharacterClass) =>
      updateManualMemberClass(
        team.id,
        member.characterReference?.id as number,
        characterClass,
      ),
    onSuccess: (newData) => {
      queryClient.setQueryData(['team', { path: team.path }], newData)
      setOpen(false)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })
  const { mutate: claim, isPending: claiming } =
    useClaimExistingManualCharacter()
  const { refreshing, refreshData } = useRefreshCharacterDetails(
    member.characterReference,
    {
      retriever: updateCharacter,
      id: member.characterReference?.id,
      successKey: 'dashboard.character.character-update-success',
      callback: () => {
        queryClient.invalidateQueries({
          queryKey: ['teamCharacterDetails', { id: team.id }],
        })
        queryClient.invalidateQueries({
          queryKey: ['team', { path: team.path }],
        })
        setOpen(false)
      },
    },
  )

  const {
    isUpdating,
    handleRankChange,
    handleRoleChange,
    handleSpecChange,
    handleOffSpecChange,
    classSpecializations,
    rolesWithMultipleSpecs,
  } = useTeamMemberEditor(member)

  const canRefreshManually =
    user?.canRefreshManually || guild?.canRefreshManually

  const RefreshMenuItem = (
    <DropdownMenuItem
      disabled={refreshing || !canRefreshManually}
      onClick={(e) => {
        e.preventDefault()
        if (refreshing) return
        refreshData()
      }}
    >
      {refreshing ? (
        <span className='flex items-center gap-2'>
          <Loader2 className='w-4 h-4 animate-spin' />
          {t('shared.refreshing')}
        </span>
      ) : (
        t('dashboard.team.roster.refresh-details')
      )}
    </DropdownMenuItem>
  )

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger disabled={isUpdating}>
        <div className='flex items-center justify-center cursor-pointer p-2 md:p-1'>
          {isUpdating ? (
            <Loader2
              className={`${
                size === 'small' ? 'w-3.5 h-3.5' : 'w-5 h-5'
              } animate-spin`}
            />
          ) : (
            <EllipsisVertical size={size === 'small' ? 14 : 18} />
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align='end'
        alignOffset={-10}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>
          {member.characterReference ? (
            <div className='flex items-center gap-2'>
              <CharacterAvatar
                character={member.characterReference}
                className='h-8 w-8'
              />

              <div>
                <Link
                  to={member.characterReference.path}
                  className='flex items-center gap-1.5'
                >
                  <CharacterClassIcon
                    characterClass={member.characterReference.characterClass}
                    size='small'
                  />
                  {member.characterReference.name}
                </Link>

                <div className='opacity-70 font-normal text-[0.7rem] mt-[-0.2rem]'>
                  {member.characterReference.realm.name} (
                  <span>{member.characterReference.realm.region}</span>)
                </div>
              </div>
            </div>
          ) : null}

          <div className='opacity-70 italic font-normal mt-2'>
            Added {member.trackingRuleId ? 'automatically' : 'manually'}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <div className='flex flex-col items-start mt-[-0.1rem]'>
              <span className='grow'>Set rank</span>
              <span className='text-xs text-muted-foreground'>
                {
                  team.teamRanks?.find((rank) => rank.id === member.teamRankId)
                    ?.name
                }
              </span>
            </div>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <RankMenuItems
                member={member}
                onRankChange={(rankId) => {
                  handleRankChange(rankId)
                  setOpen(false)
                }}
              />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <div className='flex flex-col items-start mt-[-0.1rem]'>
              <span className='grow'>Set main spec</span>

              {member.specId ? (
                <span className='text-xs text-muted-foreground flex items-center gap-1.5'>
                  <BlizzardIcon
                    iconName={classSpecializations[member.specId].icon}
                    size='small'
                  />
                  {classSpecializations[member.specId].name}
                </span>
              ) : (
                <span className='text-xs text-muted-foreground flex items-center gap-1.5'>
                  <CharacterRoleIcon
                    role={member.role ?? 'melee'}
                    size='small'
                  />
                  {capitalize(member.role)}
                </span>
              )}
            </div>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <MainSpecMenuItems
                member={member}
                classSpecializations={classSpecializations}
                rolesWithMultipleSpecs={rolesWithMultipleSpecs}
                onRoleChange={(role) => {
                  handleRoleChange(role)
                  setOpen(false)
                }}
                onSpecChange={(specId) => {
                  handleSpecChange(specId)
                  setOpen(false)
                }}
              />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {!member.specId &&
        Object.values(classSpecializations).every(
          (spec) => spec.default_role === member.role,
        ) ? null : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <div className='flex flex-col items-start mt-[-0.1rem]'>
                <span className='grow'>Set off specs</span>
                <span className='text-xs text-muted-foreground'>
                  {member.offspecIds?.length ? (
                    <div className='flex items-center gap-1.5 flex-wrap'>
                      {member.offspecIds?.map((specId) => (
                        <BlizzardIcon
                          key={specId}
                          iconName={classSpecializations[specId].icon}
                          size='small'
                        />
                      ))}
                    </div>
                  ) : null}
                </span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <OffSpecMenuItems
                  member={member}
                  classSpecializations={classSpecializations}
                  onOffSpecChange={(specId) => {
                    handleOffSpecChange(specId)
                    setOpen(false)
                  }}
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}

        {gameVersion.has('manual_characters') ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={changingClass}>
              <div className='flex flex-col items-start mt-[-0.1rem]'>
                <span className='grow'>
                  {t('dashboard.team.roster.change-class')}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {member.characterReference?.characterClass}
                </span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <ClassMenuItems
                  currentClass={member.characterReference?.characterClass}
                  onClassChange={changeClass}
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ) : null}

        {!gameVersion.has('blizzard_api') ? null : canRefreshManually ? (
          RefreshMenuItem
        ) : (
          <Tooltip>
            <TooltipTrigger className='cursor-not-allowed'>
              {RefreshMenuItem}
            </TooltipTrigger>
            <TooltipContent className='flex flex-col gap-1'>
              <p>
                Refreshing on demand is only available for users with a
                subscription tier of at least{' '}
                <span className='font-semibold text-quality-epic'>Epic</span>.
              </p>
              <p>
                This character will update on a schedule automatically (check
                the refresh status on the Audit page).
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {gameVersion.has('manual_characters') &&
        member.characterReference &&
        member.characterReference.userId === null ? (
          <DropdownMenuItem
            disabled={claiming}
            onSelect={() =>
              member.characterReference && claim(member.characterReference)
            }
          >
            Claim as mine
          </DropdownMenuItem>
        ) : null}

        {member.id && (
          <DropdownMenuItem
            disabled={removing}
            onClick={(e) => {
              e.preventDefault()
              if (removing) return
              removeFromTeam(member.id as number)
            }}
          >
            {removing ? (
              <span className='flex items-center gap-2'>
                <Loader2 className='w-4 h-4 animate-spin' />
                {t('shared.removing')}
              </span>
            ) : (
              <span className='text-destructive-foreground'>
                {member.trackingRuleId
                  ? t('dashboard.team.roster.remove-and-blacklist')
                  : t('shared.remove')}
              </span>
            )}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TeamMemberContextMenu
